import React, { useState } from 'react';
import { Upload, Progress, Button, Space, List, Typography, App } from 'antd';
import {
  InboxOutlined,
  CameraOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FileZipOutlined,
  FileUnknownOutlined,
} from '@ant-design/icons';
import { trpc } from '../../lib/api';
import type { UploadProps } from 'antd';
import { getErrorMessage } from '../../utils/errorMessage';
import { CameraCapture } from './CameraCapture';
import { compressImage } from '../../utils/imageCompression';
import { validateFile } from '../../utils/fileValidation';

const { Text } = Typography;

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.');
  if (parts.length < 2) return '';
  return parts.pop()?.toLowerCase() ?? '';
};

const getFileIcon = (file: File) => {
  const ext = getFileExtension(file.name);
  const mime = file.type;

  if (mime.startsWith('image/')) {
    return <FileImageOutlined style={{ fontSize: 24, color: '#13c2c2' }} />;
  }

  if (ext === 'pdf' || mime === 'application/pdf') {
    return <FilePdfOutlined style={{ fontSize: 24, color: '#cf1322' }} />;
  }

  if (
    ['xls', 'xlsx', 'xlsm', 'csv'].includes(ext) ||
    mime.includes('spreadsheet') ||
    mime.includes('ms-excel')
  ) {
    return <FileExcelOutlined style={{ fontSize: 24, color: '#389e0d' }} />;
  }

  if (
    ['doc', 'docx'].includes(ext) ||
    mime.includes('wordprocessingml') ||
    mime.includes('msword')
  ) {
    return <FileWordOutlined style={{ fontSize: 24, color: '#1d39c4' }} />;
  }

  if (
    ['ppt', 'pptx'].includes(ext) ||
    mime.includes('presentationml') ||
    mime.includes('ms-powerpoint')
  ) {
    return <FilePptOutlined style={{ fontSize: 24, color: '#d46b08' }} />;
  }

  if (['zip', 'rar', '7z'].includes(ext)) {
    return <FileZipOutlined style={{ fontSize: 24, color: '#531dab' }} />;
  }

  if (['txt', 'md', 'rtf'].includes(ext)) {
    return <FileTextOutlined style={{ fontSize: 24, color: '#595959' }} />;
  }

  return <FileUnknownOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />;
};

type UploadedFileResult = {
  id?: string;
  fileName?: string | null;
  originalName?: string | null;
  name?: string | null;
};

interface FileUploaderProps {
  entityType?: string;
  entityId?: string;
  category?: string;
  maxFiles?: number;
  maxSize?: number; // Maximum size in MB
  accept?: string;
  acceptLabel?: string;
  enableCamera?: boolean;
  onUploadComplete?: () => void;
  onUploadSuccess?: (file: UploadedFileResult) => void;
  disallowDuplicateNames?: boolean;
  existingFileNames?: string[];
  normalizeDuplicateName?: (name: string) => string;
}

interface UploadingFile {
  uid: string;
  name: string;
  status: 'uploading' | 'done' | 'error';
  progress: number;
  file: File;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  entityType,
  entityId,
  category = 'general',
  maxFiles = 10,
  maxSize = 10,
  accept = 'image/*,video/*',
  acceptLabel,
  enableCamera = false,
  onUploadComplete,
  onUploadSuccess,
  disallowDuplicateNames = false,
  existingFileNames = [],
  normalizeDuplicateName,
}) => {
  const { message } = App.useApp();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);

  const uploadViaServerMutation = trpc.file.uploadViaServer.useMutation();

  const normalizeName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    return normalizeDuplicateName ? normalizeDuplicateName(trimmed) : trimmed.toLowerCase();
  };

  const hasDuplicateName = (name: string) => {
    if (!disallowDuplicateNames) return false;
    const target = normalizeName(name);
    if (!target) return false;
    const normalizedExisting = new Set<string>();
    uploadingFiles.forEach((file) => normalizedExisting.add(normalizeName(file.name)));
    existingFileNames.forEach((fileName) => normalizedExisting.add(normalizeName(fileName)));
    return normalizedExisting.has(target);
  };

  const handleUpload = async (file: File) => {
    if (uploadingFiles.length >= maxFiles) {
      message.error(`一度にアップロードできるファイルは${maxFiles}件までです`);
      return;
    }

    if (hasDuplicateName(file.name)) {
      message.error('同名ファイルはアップロードできません');
      return;
    }

    const validation = validateFile(file, {
      maxSizeMB: maxSize,
      allowedTypes: accept.split(',').map((t) => t.trim()),
    });

    if (!validation.valid) {
      message.error(validation.error);
      return;
    }

    const uid = `${Date.now()}-${Math.random()}`;
    const uploadingFile: UploadingFile = {
      uid,
      name: file.name,
      status: 'uploading',
      progress: 0,
      file,
    };

    setUploadingFiles((prev) => [...prev, uploadingFile]);

    try {
      let processedFile = file;
      if (file.type.startsWith('image/')) {
        try {
          processedFile = await compressImage(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
          });
        } catch (error) {
          console.warn('画像の圧縮に失敗しました。元のファイルでアップロードを続行します:', error);
        }
      }

      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64String = result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(processedFile);
      });

      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.uid === uid ? { ...f, progress: 50 } : f
        )
      );

      const associations =
        entityType && entityId
          ? [{ entityType, entityId }]
          : undefined;

      const result = await uploadViaServerMutation.mutateAsync({
        fileName: processedFile.name,
        fileType: processedFile.type,
        fileSize: processedFile.size,
        fileData: base64Data,
        category,
        associations,
      });
      onUploadSuccess?.(result);

      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.uid === uid ? { ...f, status: 'done', progress: 100 } : f
        )
      );

      message.success(`${file.name} がアップロードされました`);
    } catch (error: unknown) {
      setUploadingFiles((prev) =>
        prev.map((f) => (f.uid === uid ? { ...f, status: 'error' as const } : f))
      );
      message.error(`${file.name} のアップロードに失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    }
  };

  const handleRemove = (uid: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleCameraCapture = (file: File) => {
    handleUpload(file);
    setCameraOpen(false);
  };

  const handleCompleteAll = () => {
    const allDone = uploadingFiles.every((f) => f.status === 'done');
    if (!allDone) {
      message.warning('まだアップロード中のファイルがあります');
      return;
    }

    setUploadingFiles([]);
    onUploadComplete?.();
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    accept,
    showUploadList: false,
    beforeUpload: (file) => {
      handleUpload(file as File);
      return false;
    },
    disabled: uploadingFiles.length >= maxFiles,
    onDrop: (event) => {
      event.stopPropagation();
    },
  };

  const allDone = uploadingFiles.length > 0 && uploadingFiles.every((f) => f.status === 'done');
  const acceptHint = acceptLabel ?? accept;

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {enableCamera && (
          <Button
            icon={<CameraOutlined />}
            onClick={() => setCameraOpen(true)}
            block
            size="large"
            disabled={uploadingFiles.length >= maxFiles}
          >
            カメラで撮影
          </Button>
        )}

        <Upload.Dragger {...uploadProps} style={{ width: '100%', maxWidth: '100%' }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">
            クリックまたはドラッグしてファイルをアップロード
          </p>
          <p className="ant-upload-hint" style={{ wordBreak: 'break-word' }}>
            最大 {maxFiles} ファイル • 最大ファイルサイズ: {maxSize} MB
            {acceptHint && ` • 対応形式: ${acceptHint}`}
          </p>
        </Upload.Dragger>

        {uploadingFiles.length > 0 && (
          <List
            dataSource={uploadingFiles}
            renderItem={(item) => (
              <List.Item
                actions={[
                  item.status === 'done' || item.status === 'error' ? (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemove(item.uid)}
                    />
                  ) : null,
                ]}
              >
                <List.Item.Meta
                  avatar={getFileIcon(item.file)}
                  title={item.name}
                  description={
                    item.status === 'uploading' ? (
                      <Progress percent={item.progress} size="small" />
                    ) : item.status === 'done' ? (
                      <Text type="success">アップロード完了</Text>
                    ) : (
                      <Text type="danger">アップロード失敗</Text>
                    )
                  }
                />
              </List.Item>
            )}
          />
        )}

        {allDone && (
          <Button type="primary" onClick={handleCompleteAll} block>
            完了
          </Button>
        )}
      </Space>

      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
};
